import { Row, Col, Button } from "reactstrap"
import { useMemo } from "react"

const TableLocalPagination = ({
  data = [],
  itemsPerPage = 10,
  currentPage = 1,
  totalPages,
  onPageChange,
  onItemsPerPageChange,
  showItemsPerPage = true,
  itemsPerPageOptions = [5, 10, 20, 50],
  paginationWrapperClass = "dataTables_paginate paging_simple_numbers pagination-rounded",
  paginationClass = "pagination justify-content-end",
  infoText = "Showing {start} to {end} of {total} entries",
  showInfo = true
}) => {
  const totalItems = data.length

  // Calculate pagination info
  const paginationInfo = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = Math.min(startIndex + itemsPerPage, totalItems)
    
    return {
      start: totalItems > 0 ? startIndex + 1 : 0,
      end: endIndex,
      total: totalItems
    }
  }, [currentPage, itemsPerPage, totalItems])

  const { start, end, total } = paginationInfo

  // Calculate total pages if not provided
  const calculatedTotalPages = totalPages || Math.ceil(totalItems / itemsPerPage)

  // Generate page numbers with ellipsis
  const generatePageNumbers = () => {
    if (calculatedTotalPages <= 1) return [1]
    
    const delta = 1
    const pages = []
    const left = currentPage - delta
    const right = currentPage + delta

    for (let i = 1; i <= calculatedTotalPages; i++) {
      if (
        i === 1 ||
        i === calculatedTotalPages ||
        (i >= left && i <= right) ||
        (currentPage <= 3 && i <= 4) ||
        (currentPage >= calculatedTotalPages - 2 && i >= calculatedTotalPages - 3)
      ) {
        pages.push(i)
      } else if (pages[pages.length - 1] !== "...") {
        pages.push("...")
      }
    }

    return pages
  }

  const pageNumbers = generatePageNumbers()

  // Handle items per page change
  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = Number(e.target.value)
    onItemsPerPageChange?.(newItemsPerPage)
    
    // Reset to first page when items per page changes
    onPageChange?.(1)
  }

  if (calculatedTotalPages <= 1 && !showItemsPerPage) return null

  return (
    <Row className="align-items-center mt-3">
      {showInfo && (
        <Col sm={12} md={showItemsPerPage ? 4 : 6}>
          <div className="dataTables_info">
            {infoText
              .replace('{start}', start)
              .replace('{end}', end)
              .replace('{total}', total)}
          </div>
        </Col>
      )}
      
      {showItemsPerPage && (
        <Col sm={12} md={4}>
          <div className="d-flex align-items-center">
            <label htmlFor="itemsPerPage" className="form-label mb-0 me-2">
              Show:
            </label>
            <select
              id="itemsPerPage"
              className="form-select form-select-sm"
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              style={{ width: "auto" }}
            >
              {itemsPerPageOptions.map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        </Col>
      )}
      
      {calculatedTotalPages > 1 && (
        <Col sm={12} md={showItemsPerPage ? 4 : 6}>
          <div className={paginationWrapperClass}>
            <ul className={paginationClass}>
              {/* Previous Button */}
              <li
                className={`paginate_button page-item previous ${
                  currentPage <= 1 ? "disabled" : ""
                }`}
              >
                <Button
                  color="link"
                  className="page-link"
                  onClick={() => onPageChange?.(Math.max(currentPage - 1, 1))}
                  disabled={currentPage <= 1}
                >
                  <i className="mdi mdi-chevron-left"></i>
                </Button>
              </li>

              {/* Page Number Buttons */}
              {pageNumbers.map((page, index) => (
                <li
                  key={index}
                  className={`paginate_button page-item ${
                    page === currentPage ? "active" : ""
                  } ${page === "..." ? "disabled" : ""}`}
                >
                  {page === "..." ? (
                    <span className="page-link">...</span>
                  ) : (
                    <Button
                      color="link"
                      className="page-link"
                      onClick={() => onPageChange?.(page)}
                    >
                      {page}
                    </Button>
                  )}
                </li>
              ))}

              {/* Next Button */}
              <li
                className={`paginate_button page-item next ${
                  currentPage >= calculatedTotalPages ? "disabled" : ""
                }`}
              >
                <Button
                  color="link"
                  className="page-link"
                  onClick={() => onPageChange?.(Math.min(currentPage + 1, calculatedTotalPages))}
                  disabled={currentPage >= calculatedTotalPages}
                >
                  <i className="mdi mdi-chevron-right"></i>
                </Button>
              </li>
            </ul>
          </div>
        </Col>
      )}
    </Row>
  )
}

export default TableLocalPagination